import {ActionList, ActionMenu, Button} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {FilterOption} from '../../utils/filter-utils'

export function AgentMultiSelectFilter({
  agentOptions,
  selectedAgents,
  onAgentChange,
  className,
  nested,
}: {
  agentOptions: FilterOption[]
  selectedAgents: string[]
  onAgentChange: (agents: string[]) => void
  className?: string
  nested?: boolean
}) {
  if (agentOptions.length <= 1) return null

  const handleSelect = (event: React.MouseEvent | React.KeyboardEvent, value: string) => {
    // Prevent ActionList.Item's afterSelect from firing, which would close the menu
    event.preventDefault()
    if (selectedAgents.includes(value)) {
      onAgentChange(selectedAgents.filter(selected => selected !== value))
    } else {
      onAgentChange([...selectedAgents, value])
    }
  }

  const agentGroup = agentOptions.filter(opt => opt.group !== 'custom')
  const customGroup = agentOptions.filter(opt => opt.group === 'custom')

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {nested ? (
          <ActionList.Item role="menuitem" aria-label="Agent">
            Agent...
          </ActionList.Item>
        ) : (
          <Button
            variant="invisible"
            trailingVisual={TriangleDownIcon}
            className={className}
            count={selectedAgents.length || undefined}
          >
            Agent
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        <ActionList selectionVariant="multiple">
          {agentGroup.length > 0 && (
            <ActionList.Group>
              <ActionList.GroupHeading>Agents</ActionList.GroupHeading>
              {agentGroup.map(opt => (
                <AgentFilterItem
                  key={opt.value}
                  option={opt}
                  selected={selectedAgents.includes(opt.value)}
                  onSelect={e => handleSelect(e, opt.value)}
                />
              ))}
            </ActionList.Group>
          )}
          {customGroup.length > 0 && (
            <>
              {agentGroup.length > 0 && <ActionList.Divider />}
              <ActionList.Group>
                <ActionList.GroupHeading>Custom agents</ActionList.GroupHeading>
                {customGroup.map(opt => (
                  <AgentFilterItem
                    key={opt.value}
                    option={opt}
                    selected={selectedAgents.includes(opt.value)}
                    onSelect={e => handleSelect(e, opt.value)}
                  />
                ))}
              </ActionList.Group>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function AgentFilterItem({
  option,
  selected,
  onSelect,
}: {
  option: FilterOption
  selected: boolean
  onSelect: (event: React.MouseEvent | React.KeyboardEvent) => void
}) {
  return (
    <ActionList.Item selected={selected} onSelect={onSelect}>
      {(option.icon || option.avatarUrl) && (
        <ActionList.LeadingVisual>
          {option.icon ? <option.icon /> : option.avatarUrl && <GitHubAvatar src={option.avatarUrl} size={16} square />}
        </ActionList.LeadingVisual>
      )}
      {option.label}
    </ActionList.Item>
  )
}
